import { FormEvent, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { MobileFrame } from "@/components/MobileFrame";
import { Icon } from "@/components/primitives";
import { useAuth } from "@/hooks/useAuth";
import { useModeratorStatus } from "@/hooks/useModeratorStatus";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/lib/toast";

const ink = "hsl(var(--ink))";
const paper = "hsl(var(--paper))";

const labelStyle: React.CSSProperties = {
  display: "block",
  fontFamily: "'IBM Plex Mono', monospace",
  fontSize: 11,
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  marginBottom: 6,
  opacity: 0.7,
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "10px 12px",
  border: `1px solid ${ink}`,
  background: paper,
  color: ink,
  marginBottom: 16,
  fontFamily: "'IBM Plex Mono', monospace",
  fontSize: 13,
};

export default function ModeratorApplication() {
  const nav = useNavigate();
  const { session } = useAuth();
  const { data: state, refetch } = useModeratorStatus();
  const [yearsCollecting, setYearsCollecting] = useState("");
  const [specialty, setSpecialty] = useState("");
  const [closetPieces, setClosetPieces] = useState("");
  const [motivation, setMotivation] = useState("");
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!session?.user?.id) {
      toast.error("Sign in required");
      return;
    }
    if (!yearsCollecting.trim() || !specialty.trim() || !closetPieces.trim() || !motivation.trim()) {
      toast.error("All four questions are required");
      return;
    }
    setBusy(true);
    const { error } = await supabase.from("moderator_applications").insert({
      user_id: session.user.id,
      years_collecting: yearsCollecting.trim(),
      specialty: specialty.trim(),
      closet_pieces: closetPieces.trim(),
      motivation: motivation.trim(),
    });
    setBusy(false);
    if (error) {
      toast.error(error.message ?? "Couldn't submit application");
      return;
    }
    toast.success("Application submitted. We'll review and notify you.");
    await refetch();
    nav("/settings");
  };

  // Render based on state
  if (!state || state.status === "loading") {
    return (
      <MobileFrame>
        <div style={{ padding: 24, color: ink }}>Loading…</div>
      </MobileFrame>
    );
  }

  if (state.status === "not-eligible") {
    return (
      <MobileFrame>
        <ChromeHeader onBack={() => nav(-1)} />
        <div style={{ padding: "8px 20px 32px", color: ink, lineHeight: 1.5 }}>
          <h1 style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: 28, marginBottom: 10 }}>
            Not eligible yet
          </h1>
          <p style={{ marginBottom: 16 }}>{state.reason}</p>
          <p style={{ marginBottom: 12, opacity: 0.75, fontSize: 14 }}>
            Verified collectors help FIG keep fast fashion off the platform and weigh
            in on tough verdicts. To qualify you need:
          </p>
          <ul style={{ marginBottom: 20, paddingLeft: 18, lineHeight: 1.7 }}>
            <li>An active Seller or Pro Seller subscription</li>
            <li>10 PASS-verified posts in your closet</li>
          </ul>
          <button onClick={() => nav("/settings")} style={ghostButtonStyle}>
            Back to settings
          </button>
        </div>
      </MobileFrame>
    );
  }

  if (state.status === "pending") {
    return (
      <MobileFrame>
        <ChromeHeader onBack={() => nav(-1)} />
        <div style={{ padding: "8px 20px 32px", color: ink, lineHeight: 1.5 }}>
          <h1 style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: 28, marginBottom: 10 }}>
            Application pending
          </h1>
          <p style={{ marginBottom: 14 }}>
            Thanks for applying. We read every submission by hand, so reviews can take
            a few days — we'll send a notification the moment there's a decision.
          </p>
          <p style={{ opacity: 0.7, fontSize: 14 }}>
            In the meantime, keep scanning. The strength and breadth of your closet is
            one of the signals we weigh.
          </p>
        </div>
      </MobileFrame>
    );
  }

  if (state.status === "approved") {
    return (
      <MobileFrame>
        <ChromeHeader onBack={() => nav(-1)} />
        <div style={{ padding: "8px 20px 40px", color: ink, lineHeight: 1.55 }}>

          {/* Hero */}
          <h1 style={{ fontFamily: "'Fraunces', serif", fontWeight: 800, fontSize: 30, lineHeight: 1.05, marginBottom: 8, letterSpacing: "-0.01em" }}>
            You're a verified collector.
          </h1>
          <p style={{ marginBottom: 22, fontSize: 15 }}>
            Welcome to the moderation circle. The hive runs on collectors like you —
            your eye is how FIG stays trustworthy. Here's everything you can do now,
            plus what we expect in return.
          </p>

          {/* What you can do */}
          <SectionCard title="What you can do">
            <ModBullet
              heading="Vote on other collectors' posts"
              body="Every verified post from someone else now shows Vintage / Not vintage buttons. Take a careful look at the photos, then cast a vote. Your votes are anonymous and aggregate into a 'hive consensus' chip the poster and FIG admins can see."
            />
            <ModBullet
              heading="Flag fast fashion"
              body="If a post is clearly fast-fashion or mass-modern slipping past the AI, tap the post's overflow menu and choose Report fast fashion. Three flags from different moderators auto-suspends the post for FIG admin review."
            />
            <ModBullet
              heading="Earn +5 free scans every month"
              body="On your first scan each calendar month we add 5 free scans on top of your subscription cap. No action needed — it happens automatically as long as your subscription stays active."
              tone="bright"
            />
          </SectionCard>

          {/* Expectations */}
          <SectionCard title="What we ask of you">
            <ModBullet
              heading="Be specific and kind"
              body="When you vote 'not vintage,' you're telling a real collector their piece doesn't fit. Make sure you've looked at every photo before you flag. The community works because we're rigorous, not because we're picky."
            />
            <ModBullet
              heading="Lean on the reference doc, not vibes"
              body="If you're unsure on era or brand, default to vintage. Disagreements with FIG's verdict are useful signal even if you turn out to be wrong — we learn from your notes."
            />
            <ModBullet
              heading="Don't moderate your own circle"
              body="You can't vote on your own posts (the buttons won't appear). For posts from people you know, use extra care so personal taste doesn't bleed into the call."
            />
          </SectionCard>

          {/* Activity review — the policy ask */}
          <SectionCard title="How we keep the circle strong" tone="muted">
            <p style={{ fontSize: 14, marginBottom: 12 }}>
              Verified status is an active role, not a permanent badge. We review
              moderator activity periodically. If a verified collector hasn't reviewed
              or voted on any posts in <strong>90 days</strong>, we reserve the right
              to revoke verified status to keep the role meaningful.
            </p>
            <p style={{ fontSize: 14, marginBottom: 8 }}>
              We'll always reach out before revoking and you can re-apply anytime
              once you're back in the rhythm.
            </p>
          </SectionCard>

          {/* Where to start */}
          <SectionCard title="Where to start">
            <p style={{ fontSize: 14, marginBottom: 12 }}>
              Open your feed and scroll the Nearby or Following tab. Tap into any post
              that catches your eye — the moderator vote bar sits right below the
              action pills.
            </p>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => nav("/feed")} style={primaryButtonStyle}>
                Go to feed
              </button>
              <button onClick={() => nav("/settings")} style={ghostButtonStyle}>
                Back to settings
              </button>
            </div>
          </SectionCard>

          <p style={{ opacity: 0.55, fontSize: 12, marginTop: 18, lineHeight: 1.5 }}>
            Thank you for the time you give to this. The brand exists because
            collectors care enough to do the work nobody pays them for.
          </p>
        </div>
      </MobileFrame>
    );
  }

  if (state.status === "rejected") {
    return (
      <MobileFrame>
        <ChromeHeader onBack={() => nav(-1)} />
        <div style={{ padding: "8px 20px 32px", color: ink, lineHeight: 1.5 }}>
          <h1 style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: 28, marginBottom: 10 }}>
            Application not approved
          </h1>
          {state.notes && (
            <div style={{
              padding: 12, marginBottom: 16,
              border: `1px dashed ${ink}`,
              background: "hsl(var(--paper-2))",
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 12,
              whiteSpace: "pre-wrap",
            }}>
              Notes from the reviewer: {state.notes}
            </div>
          )}
          <p>
            You can re-apply once your circumstances change — more verified posts,
            a longer track record, or anything the reviewer flagged.
          </p>
          <div style={{ marginTop: 18 }}>
            <button onClick={() => nav("/settings")} style={ghostButtonStyle}>
              Back to settings
            </button>
          </div>
        </div>
      </MobileFrame>
    );
  }

  // status === "eligible" — render the form
  return (
    <MobileFrame>
      <ChromeHeader onBack={() => nav(-1)} />
      <form onSubmit={handleSubmit} style={{ padding: "8px 20px 32px", color: ink }}>
        <h1 style={{ fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: 28, marginBottom: 8 }}>
          Become a fashion moderator
        </h1>
        <p style={{ marginBottom: 20, opacity: 0.7, fontSize: 14, lineHeight: 1.5 }}>
          Verified moderators help fig keep fast fashion off the platform. Three flags from moderators auto-suspends a post for fig admin review.
        </p>

        <label style={labelStyle}>How long have you been collecting?</label>
        <input
          style={inputStyle}
          value={yearsCollecting}
          onChange={(e) => setYearsCollecting(e.target.value)}
          disabled={busy}
          placeholder="e.g. 8 years"
          required
        />

        <label style={labelStyle}>What categories or eras do you specialize in?</label>
        <input
          style={inputStyle}
          value={specialty}
          onChange={(e) => setSpecialty(e.target.value)}
          disabled={busy}
          placeholder="e.g. 1970s denim, 1990s workwear"
          required
        />

        <label style={labelStyle}>Link your closet / describe a few pieces you're proud of</label>
        <textarea
          style={{ ...inputStyle, minHeight: 80 }}
          value={closetPieces}
          onChange={(e) => setClosetPieces(e.target.value)}
          disabled={busy}
          placeholder="Tell us about a few of your favorite pieces."
          rows={3}
          required
        />

        <label style={labelStyle}>Why do you want to be a verified moderator?</label>
        <textarea
          style={{ ...inputStyle, minHeight: 80 }}
          value={motivation}
          onChange={(e) => setMotivation(e.target.value)}
          disabled={busy}
          placeholder="What's your motivation for helping fig?"
          rows={3}
          required
        />

        <button
          type="submit"
          disabled={busy}
          style={{
            width: "100%",
            padding: "12px",
            border: `1px solid ${ink}`,
            background: ink,
            color: paper,
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 12,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            cursor: busy ? "wait" : "pointer",
            opacity: busy ? 0.7 : 1,
          }}
        >
          {busy ? "Submitting…" : "Submit application"}
        </button>
      </form>
    </MobileFrame>
  );
}

// ── Shared chrome ─────────────────────────────────────────────────────────

const primaryButtonStyle: React.CSSProperties = {
  padding: "10px 16px",
  border: `1px solid ${ink}`,
  background: ink,
  color: "hsl(var(--ink-fg))",
  fontFamily: "'IBM Plex Mono', monospace",
  fontSize: 11,
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  cursor: "pointer",
};

const ghostButtonStyle: React.CSSProperties = {
  padding: "10px 16px",
  border: `1px solid ${ink}`,
  background: paper,
  color: ink,
  fontFamily: "'IBM Plex Mono', monospace",
  fontSize: 11,
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  cursor: "pointer",
};

function ChromeHeader({ onBack }: { onBack: () => void }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 8,
      padding: "14px 14px 6px",
    }}>
      <button
        onClick={onBack}
        aria-label="Back"
        style={{
          background: "none", border: "none", padding: 6,
          cursor: "pointer", color: ink,
        }}
      >
        {Icon.chev(18, "l")}
      </button>
      <span style={{
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase",
        color: "hsl(var(--ink-2))",
      }}>
        Verified collector
      </span>
    </div>
  );
}

function SectionCard({
  title, children, tone = "default",
}: { title: string; children: ReactNode; tone?: "default" | "muted" | "bright" }) {
  const bg = tone === "muted" ? "hsl(var(--paper-2))"
           : tone === "bright" ? "hsl(var(--cream))"
           : "hsl(var(--paper))";
  return (
    <div style={{
      marginBottom: 16,
      padding: "14px 16px",
      background: bg,
      border: `1px solid ${ink}`,
    }}>
      <div style={{
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase",
        color: "hsl(var(--rust))",
        marginBottom: 10,
      }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function ModBullet({
  heading, body, tone = "default",
}: { heading: string; body: string; tone?: "default" | "bright" }) {
  return (
    <div style={{
      marginBottom: 12,
      paddingLeft: 12,
      borderLeft: tone === "bright"
        ? "2px solid hsl(var(--rust))"
        : "2px solid hsl(var(--sand-deep) / 0.6)",
    }}>
      <div style={{
        fontFamily: "'Fraunces', 'Playfair Display', serif",
        fontWeight: 700, fontSize: 15, color: ink, lineHeight: 1.2,
        marginBottom: 4,
      }}>
        {heading}
      </div>
      <div style={{
        fontFamily: "'Fraunces', 'Playfair Display', serif",
        fontSize: 13, color: "hsl(var(--ink-2))", lineHeight: 1.5,
      }}>
        {body}
      </div>
    </div>
  );
}
